// AdminTeams.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Plus, Edit2, Trash2, X, Loader2, Users, MapPin, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const emptyForm = { name: '', description: '', color: '#2563EB', locationId: '' };

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { fetchTeams(); fetchLocations(); }, []);
  const fetchTeams = async () => { const { data } = await api.get('/teams'); setTeams(data); };
  const fetchLocations = async () => {
    try { const { data } = await api.get('/admin/locations'); setLocations(data); } catch { /* non-admin */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, locationId: form.locationId || null };
      editing ? await api.put(`/teams/${editing}`, payload) : await api.post('/teams', payload);
      toast.success(editing ? 'Équipe mise à jour' : 'Équipe créée');
      setShowModal(false); setEditing(null); setForm(emptyForm); fetchTeams();
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette équipe ?')) return;
    await api.delete(`/teams/${id}`); toast.success('Équipe supprimée'); fetchTeams();
  };

  const openEdit = (team) => {
    setForm({
      name: team.name || '',
      description: team.description || '',
      color: team.color || '#2563EB',
      locationId: team.locationId || ''
    });
    setEditing(team.id);
    setShowModal(true);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Équipes</h1>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium">
          <Plus className="w-4 h-4" /> Nouvelle équipe
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map(team => (
          <div key={team.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: team.color + '20' }}>
                  <Users className="w-5 h-5" style={{ color: team.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{team.name}</h3>
                  <p className="text-xs text-gray-500">{team.members?.length || 0} membres</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(team)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => handleDelete(team.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {team.description && <p className="text-gray-500 text-sm">{team.description}</p>}
            <div className="mt-3">
              {team.zone ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <MapPin className="w-3.5 h-3.5" /> {team.zone.name} · {team.zone.radius} m
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full">
                  <AlertTriangle className="w-3.5 h-3.5" /> Aucune zone — pointage bloqué
                </span>
              )}
            </div>
            {team.members?.length > 0 && (
              <div className="flex -space-x-1 mt-3">
                {team.members.slice(0, 5).map(m => (
                  <div key={m.id} className="w-7 h-7 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-xs font-bold text-blue-600">
                    {m.firstName?.[0]}{m.lastName?.[0]}
                  </div>
                ))}
                {team.members.length > 5 && <div className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs text-gray-600">+{team.members.length - 5}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-800">{editing ? 'Modifier l\'équipe' : 'Nouvelle équipe'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom *</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Zone de pointage</label>
                <select value={form.locationId} onChange={e => setForm(p => ({...p, locationId: e.target.value}))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                  <option value="">— Aucune zone (pointage bloqué) —</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name} ({loc.radius} m)</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">
                  Les agents de ce groupe ne pourront pointer que dans cette zone. Créez des zones dans « Localisations & Géofencing ».
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Couleur</label>
                <input type="color" value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} className="w-12 h-10 border border-gray-200 rounded-lg cursor-pointer" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium">Annuler</button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
